import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Heart,
  Users,
  UserPlus,
  UserMinus,
  Send,
  Check,
  X,
  Loader2,
  PawPrint,
  MessageSquare,
  Clock,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PetFriends = () => {
  const { user, token } = useAuth();
  const [myPets, setMyPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState(null);
  const [friends, setFriends] = useState([]);
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [potentialFriends, setPotentialFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendRequestOpen, setSendRequestOpen] = useState(false);
  const [targetPet, setTargetPet] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (user && token) {
      fetchMyPets();
      fetchRequests();
    }
  }, [user, token]);

  useEffect(() => {
    if (selectedPet) {
      fetchPetFriends(selectedPet.id);
      fetchPotentialFriends(selectedPet.id);
    }
  }, [selectedPet]);

  const fetchMyPets = async () => {
    try {
      const response = await axios.get(`${API}/pets/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPets(response.data);
      if (response.data.length > 0 && !selectedPet) {
        setSelectedPet(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching pets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const [incomingRes, outgoingRes] = await Promise.all([
        axios.get(`${API}/dogs/friends/requests/incoming`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/dogs/friends/requests/outgoing`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setIncomingRequests(incomingRes.data);
      setOutgoingRequests(outgoingRes.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const fetchPetFriends = async (petId) => {
    try {
      const response = await axios.get(`${API}/dogs/${petId}/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(response.data);
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  };

  const fetchPotentialFriends = async (petId) => {
    try {
      const response = await axios.get(`${API}/dogs/${petId}/potential-friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPotentialFriends(response.data);
    } catch (error) {
      console.error('Error fetching potential friends:', error);
    }
  };

  const handleSendRequest = async () => {
    if (!selectedPet || !targetPet) return;

    setSending(true);
    try {
      await axios.post(`${API}/dogs/friends/request`, {
        from_dog_id: selectedPet.id,
        to_dog_id: targetPet.id,
        message: requestMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Friend request sent to ${targetPet.name}!`);
      setSendRequestOpen(false);
      setTargetPet(null);
      setRequestMessage('');
      fetchRequests();
      fetchPotentialFriends(selectedPet.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSending(false);
    }
  };

  const handleAcceptRequest = async (requestId, dogName) => {
    try {
      await axios.post(`${API}/dogs/friends/requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`You're now friends with ${dogName}!`);
      fetchRequests();
      if (selectedPet) fetchPetFriends(selectedPet.id);
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await axios.post(`${API}/dogs/friends/requests/${requestId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request declined');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/dogs/friends/requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request cancelled');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const handleRemoveFriend = async (friendDogId, friendName) => {
    if (!window.confirm(`Remove ${friendName} from ${selectedPet.name}'s friends?`)) return;

    try {
      await axios.delete(`${API}/dogs/${selectedPet.id}/friends/${friendDogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${friendName} removed from friends`);
      fetchPetFriends(selectedPet.id);
    } catch (error) {
      toast.error('Failed to remove friend');
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <PawPrint className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
        <h2 className="text-2xl font-heading mb-4">Sign in Required</h2>
        <p className="text-muted-foreground">Please sign in to manage pet friendships.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
      </div>
    );
  }

  if (myPets.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <PawPrint className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
        <h2 className="text-2xl font-heading mb-4">No Pets Yet</h2>
        <p className="text-muted-foreground mb-4">Add a pet first to start making friends!</p>
        <Button onClick={() => window.location.href = '/my-pets'}>Add a Pet</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-background py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Heart className="w-8 h-8 text-primary" />
              Pet Friends
            </h1>
            <p className="text-muted-foreground">
              Connect your pets with other dogs in the community
            </p>
          </div>

          {/* Pet Selector */}
          <div className="flex items-center gap-4">
            <Select 
              value={selectedPet?.id || ''} 
              onValueChange={(id) => setSelectedPet(myPets.find(p => p.id === id))}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select your pet" />
              </SelectTrigger>
              <SelectContent>
                {myPets.map(pet => (
                  <SelectItem key={pet.id} value={pet.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={pet.image_url} />
                        <AvatarFallback>{pet.name[0]}</AvatarFallback>
                      </Avatar>
                      {pet.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Request Badges */}
        {(incomingRequests.length > 0 || outgoingRequests.length > 0) && (
          <div className="flex gap-4 mb-6">
            {incomingRequests.length > 0 && (
              <Badge variant="default" className="bg-green-500">
                {incomingRequests.length} incoming request{incomingRequests.length !== 1 ? 's' : ''}
              </Badge>
            )}
            {outgoingRequests.length > 0 && (
              <Badge variant="secondary">
                {outgoingRequests.length} pending request{outgoingRequests.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        )}

        <Tabs defaultValue="friends" className="space-y-6">
          <TabsList>
            <TabsTrigger value="friends" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Friends ({friends.length})
            </TabsTrigger>
            <TabsTrigger value="requests" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Requests ({incomingRequests.length + outgoingRequests.length})
            </TabsTrigger>
            <TabsTrigger value="discover" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Discover ({potentialFriends.length})
            </TabsTrigger>
          </TabsList>

          {/* Friends Tab */}
          <TabsContent value="friends">
            {friends.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Heart className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Friends Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    {selectedPet?.name} hasn't made any friends yet. Check out the Discover tab!
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {friends.map(friend => (
                  <Card key={friend.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={friend.image_url} />
                          <AvatarFallback className="bg-primary/10 text-primary text-xl">
                            {friend.name[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-medium text-lg">{friend.name}</h3>
                          <p className="text-sm text-muted-foreground">{friend.breed}</p>
                          <p className="text-xs text-muted-foreground">
                            Owner: @{friend.owner_username}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            Friends since {new Date(friend.friendship_since).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-3 text-destructive hover:text-destructive"
                        onClick={() => handleRemoveFriend(friend.id, friend.name)}
                      >
                        <UserMinus className="w-4 h-4 mr-2" />
                        Remove Friend
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests">
            <div className="space-y-6">
              {/* Incoming Requests */}
              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  Incoming Requests ({incomingRequests.length})
                </h3>
                {incomingRequests.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No incoming requests</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {incomingRequests.map(req => (
                      <Card key={req.id} className="border-green-200 bg-green-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4 mb-3">
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={req.from_dog_image_url} />
                              <AvatarFallback>{req.from_dog_name[0]}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium">
                                {req.from_dog_name} wants to be friends with {req.to_dog_name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                From @{req.from_owner_username}
                              </p>
                            </div>
                          </div>
                          {req.message && (
                            <p className="text-sm bg-white p-2 rounded mb-3 italic">
                              "{req.message}"
                            </p>
                          )}
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="flex-1"
                              onClick={() => handleAcceptRequest(req.id, req.from_dog_name)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Accept
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="flex-1"
                              onClick={() => handleDeclineRequest(req.id)}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Outgoing Requests */}
              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-blue-500" />
                  Sent Requests ({outgoingRequests.length})
                </h3>
                {outgoingRequests.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No pending requests</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {outgoingRequests.map(req => (
                      <Card key={req.id} className="border-blue-200 bg-blue-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4 mb-3">
                            <Avatar className="w-12 h-12">
                              <AvatarImage src={req.to_dog_image_url} />
                              <AvatarFallback>{req.to_dog_name[0]}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <p className="font-medium">
                                Request sent to {req.to_dog_name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Owner: @{req.to_owner_username}
                              </p>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="w-full"
                            onClick={() => handleCancelRequest(req.id)}
                          >
                            Cancel Request
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Discover Tab */}
          <TabsContent value="discover">
            {potentialFriends.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Sparkles className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-xl font-heading mb-2">No Suggestions Yet</h3>
                  <p className="text-muted-foreground">
                    Make some human friends first, and we'll suggest their pets!
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {potentialFriends.map(pet => (
                  <Card key={pet.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4 mb-3">
                        <Avatar className="w-14 h-14">
                          <AvatarImage src={pet.image_url} />
                          <AvatarFallback className="bg-amber-100 text-amber-700">
                            {pet.name[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <h3 className="font-medium">{pet.name}</h3>
                          <p className="text-sm text-muted-foreground">{pet.breed}</p>
                          <p className="text-xs text-muted-foreground">
                            @{pet.owner_username}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="mb-3 text-xs">
                        <Users className="w-3 h-3 mr-1" />
                        Owner is your friend
                      </Badge>
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setTargetPet(pet);
                          setSendRequestOpen(true);
                        }}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Send Friend Request
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Send Request Dialog */}
        <Dialog open={sendRequestOpen} onOpenChange={setSendRequestOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Friend Request</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {selectedPet && targetPet && (
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <Avatar className="w-16 h-16 mx-auto mb-2">
                      <AvatarImage src={selectedPet.image_url} />
                      <AvatarFallback>{selectedPet.name[0]}</AvatarFallback>
                    </Avatar>
                    <p className="font-medium">{selectedPet.name}</p>
                  </div>
                  <Heart className="w-6 h-6 text-pink-500" />
                  <div className="text-center">
                    <Avatar className="w-16 h-16 mx-auto mb-2">
                      <AvatarImage src={targetPet.image_url} />
                      <AvatarFallback>{targetPet.name[0]}</AvatarFallback>
                    </Avatar>
                    <p className="font-medium">{targetPet.name}</p>
                  </div>
                </div>
              )}
              <div>
                <label className="text-sm font-medium">Add a message (optional)</label>
                <Textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Say hi! Maybe mention where your pets could meet..."
                  rows={3}
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendRequestOpen(false)}>Cancel</Button>
              <Button onClick={handleSendRequest} disabled={sending}>
                {sending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Send Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default PetFriends;
